import CloseIcon from '@mui/icons-material/Close';
import { useEffect } from 'react';
import { MarketIndicatorTemplateType } from '../../../../../Generated/Raven-Demeter';
import addNewDataSource from '../../../../Assets/Icons/expandGray.svg';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { UpsertMarketIndicatorFactorRequest } from '../MarketIndicatorsManagementDefinitions';
import MarketIndicatorFactorDataDisplay from './MarketIndicatorFactorDataDisplay';
import MarketIndicatorFactorDataSource from './MarketIndicatorFactorDataSource';
import styles from './MarketIndicatorFactorPage.module.scss';
import MarketFactorMathOperationDropdown from './MarketIndicatorsFactorFields/Common/MarketFactorMathOperationDropdown';

interface IMarketIndicatorFactorDataSourceAreaProps {
    upsertMarketIndicatorFactorRequest: UpsertMarketIndicatorFactorRequest;
    setUpsertMarketIndicatorFactorRequest: (value: UpsertMarketIndicatorFactorRequest) => void;
    numberOfDataSourceSections: number;
    setNumberOfDataSourceSections: (numberOfDataSourceSections: number) => void;
    useSecondary?: boolean;
}

const MarketIndicatorFactorDataSourceArea: React.FC<IMarketIndicatorFactorDataSourceAreaProps> = (props: IMarketIndicatorFactorDataSourceAreaProps) => {
    const [translations] = useLanguage();

    const market = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondaryMarket! ?? props.upsertMarketIndicatorFactorRequest.market
        : props.upsertMarketIndicatorFactorRequest.market;
    const leadingIndicatorType = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondaryLeadingIndicatorType ?? props.upsertMarketIndicatorFactorRequest.leadingIndicatorType
        : props.upsertMarketIndicatorFactorRequest.leadingIndicatorType;
    const region = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondaryRegion ?? props.upsertMarketIndicatorFactorRequest.region
        : props.upsertMarketIndicatorFactorRequest.region;
    const subRegion = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondarySubRegion ?? props.upsertMarketIndicatorFactorRequest.subRegion
        : props.upsertMarketIndicatorFactorRequest.subRegion;
    const commodity = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondaryCommodity ?? props.upsertMarketIndicatorFactorRequest.commodity
        : props.upsertMarketIndicatorFactorRequest.commodity;
    const extraParameters = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondaryExtraParameters
        : props.upsertMarketIndicatorFactorRequest.extraParameters;
    const dataFrequency = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondaryDataFrequency ?? props.upsertMarketIndicatorFactorRequest.dataFrequency
        : props.upsertMarketIndicatorFactorRequest.dataFrequency;
    const transformationType = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondaryTransformationType
        : props.upsertMarketIndicatorFactorRequest.transformationType;
    const transformationValue = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondaryTransformationValue
        : props.upsertMarketIndicatorFactorRequest.transformationValue;
    const unitOfMeasure = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondaryUnitOfMeasure ?? props.upsertMarketIndicatorFactorRequest.unitOfMeasure
        : props.upsertMarketIndicatorFactorRequest.unitOfMeasure;
    const currency = props.useSecondary
        ? props.upsertMarketIndicatorFactorRequest.secondaryCurrency ?? props.upsertMarketIndicatorFactorRequest.currency
        : props.upsertMarketIndicatorFactorRequest.currency;

    const templateTypeIsAllowedMultipleDataSources =
        props.upsertMarketIndicatorFactorRequest?.templateType !== MarketIndicatorTemplateType.ForwardCurve &&
        props.upsertMarketIndicatorFactorRequest?.templateType !== MarketIndicatorTemplateType.Speculative;

    useEffect(() => {
        if (!templateTypeIsAllowedMultipleDataSources) {
            props.setNumberOfDataSourceSections(1);
        } else if (props.upsertMarketIndicatorFactorRequest.secondaryMarket) {
            props.setNumberOfDataSourceSections(2);
        }
    }, [props.upsertMarketIndicatorFactorRequest?.templateType]);

    return (
        <>
            <MarketIndicatorFactorDataSource
                title={
                    props.useSecondary
                        ? `${translations.marketIndicatorsManagement.headers.dataSource} 2`
                        : translations.marketIndicatorsManagement.headers.dataSource
                }
                market={market}
                region={region}
                subRegion={subRegion}
                commodity={commodity}
                extraParameters={extraParameters}
                leadingIndicatorType={leadingIndicatorType}
                templateType={props.upsertMarketIndicatorFactorRequest.templateType}
                dataFrequency={dataFrequency}
                transformationType={transformationType!}
                transformationValue={transformationValue}
                handleChange={(
                    updatedMarket,
                    updatedFundamentalCategory,
                    updatedLeadingIndicatorType,
                    updatedRegion,
                    updatedSubRegion,
                    updatedCommodity,
                    updatedExtraParameters,
                    updatedTransformationType,
                    updatedTransformationValue,
                    updatedDataFrequency,
                ) => {
                    if (props.useSecondary) {
                        props.setUpsertMarketIndicatorFactorRequest({
                            ...props.upsertMarketIndicatorFactorRequest,
                            secondaryMarket: updatedMarket,
                            secondaryFundamentalCategory: updatedFundamentalCategory,
                            secondaryLeadingIndicatorType: updatedLeadingIndicatorType,
                            secondaryRegion: updatedRegion!,
                            secondarySubRegion: updatedSubRegion,
                            secondaryCommodity: updatedCommodity,
                            secondaryExtraParameters: updatedExtraParameters,
                            secondaryTransformationType: updatedTransformationType,
                            secondaryTransformationValue: updatedTransformationValue,
                            secondaryDataFrequency: updatedDataFrequency!,
                        });
                    } else {
                        props.setUpsertMarketIndicatorFactorRequest({
                            ...props.upsertMarketIndicatorFactorRequest,
                            market: updatedMarket,
                            fundamentalCategory: updatedFundamentalCategory,
                            leadingIndicatorType: updatedLeadingIndicatorType,
                            region: updatedRegion!,
                            subRegion: updatedSubRegion,
                            commodity: updatedCommodity,
                            extraParameters: updatedExtraParameters,
                            transformationType: updatedTransformationType,
                            transformationValue: updatedTransformationValue,
                            dataFrequency: updatedDataFrequency!,
                        });
                    }
                }}
            />
            {props.upsertMarketIndicatorFactorRequest.dataCombinationType && (
                <MarketIndicatorFactorDataDisplay
                    unitOfMeasure={unitOfMeasure}
                    currency={currency}
                    dataCombinationType={props.upsertMarketIndicatorFactorRequest.dataCombinationType}
                    displayUnitOfMeasure={props.upsertMarketIndicatorFactorRequest.dataCombinationDisplayUnits}
                    useSecondary={props.useSecondary}
                    handleChange={(updatedUnitOfMeasure, updatedCurrency, updatedDataCombinationDisplayUnits) => {
                        if (props.useSecondary) {
                            props.setUpsertMarketIndicatorFactorRequest({
                                ...props.upsertMarketIndicatorFactorRequest,
                                secondaryUnitOfMeasure: updatedUnitOfMeasure,
                                secondaryCurrency: updatedCurrency,
                            });
                        } else {
                            props.setUpsertMarketIndicatorFactorRequest({
                                ...props.upsertMarketIndicatorFactorRequest,
                                unitOfMeasure: updatedUnitOfMeasure,
                                currency: updatedCurrency,
                                dataCombinationDisplayUnits: updatedDataCombinationDisplayUnits,
                                secondaryUnitOfMeasure: updatedUnitOfMeasure,
                                secondaryCurrency: updatedCurrency,
                            });
                        }
                    }}
                />
            )}

            {!props.useSecondary && props.numberOfDataSourceSections === 1 && templateTypeIsAllowedMultipleDataSources && (
                <button type="button" onClick={() => props.setNumberOfDataSourceSections(2)} className={styles.indicator_add_and_edit_add_data_source}>
                    <img src={addNewDataSource} alt="#" /> {translations.marketIndicatorsManagement.text.addDataSourceSection}
                </button>
            )}
            {!props.useSecondary && props.numberOfDataSourceSections === 2 && (
                <>
                    <div className={styles.indicator_add_and_edit_line_break} />
                    <div className={styles.indicator_add_and_edit_close_icon_container}>
                        <CloseIcon
                            onClick={() => {
                                props.setNumberOfDataSourceSections(1);

                                props.setUpsertMarketIndicatorFactorRequest({
                                    ...props.upsertMarketIndicatorFactorRequest,
                                    dataCombinationType: undefined,
                                    dataCombinationDisplayUnits: undefined,
                                    unitOfMeasure: undefined,
                                    currency: undefined,
                                    secondaryMarket: undefined,
                                    secondaryRegion: undefined,
                                    secondarySubRegion: undefined,
                                    secondaryCommodity: undefined,
                                    secondaryLeadingIndicatorType: undefined,
                                    secondaryFundamentalCategory: undefined,
                                    secondaryDataFrequency: undefined,
                                    secondaryExtraParameters: undefined,
                                    secondaryTransformationType: undefined,
                                    secondaryTransformationValue: undefined,
                                    secondaryUnitOfMeasure: undefined,
                                    secondaryCurrency: undefined,
                                });
                            }}
                        />
                    </div>
                    <MarketFactorMathOperationDropdown
                        upsertRequest={props.upsertMarketIndicatorFactorRequest}
                        setUpsertRequest={props.setUpsertMarketIndicatorFactorRequest}
                    />
                </>
            )}
        </>
    );
};

export default MarketIndicatorFactorDataSourceArea;
